import React, { useEffect, useRef, useState } from "react";
import TextInput from "../FormItems/TextInput.jsx";
import GeneralError from "../FormItems/GeneralError.jsx";
import SelectInput from "../FormItems/SelectInput.jsx";
import Autocomplete from "../FormItems/Autocomplete.jsx";
import fetchOptions from "./fetchOptions.js";

const FindOfficeForm = props => {
  const {
    label,
    submitLabel,
    city,
    stateSelect,
    distanceSelect,
    formAction = "",
    method = "post",
    defaultCity,
    defaultState,
    defaultDistance,
    defaultMarket,
    agentTypes,
    isResultsOnly,
  } = props;

  const formRef = useRef(null);
  const submitBtn = useRef(null);

  const [stateOptions, setStateOptions] = useState([]);
  const [distanceOptions, setDistanceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedState, setSelectedState] = useState(defaultState ?? "");
  const [selectedDistance, setSelectedDistance] = useState(defaultDistance ?? "",);
  const [selectedMarketArea, setSelectedMarketArea] = useState(defaultMarket ?? "",);
  const [selectedAgentTypes, setSelectedAgentTypes] = useState(agentTypes ?? "",);
  const [cityValue, setCityValue] = useState(defaultCity ?? "");
  
  useEffect(() => {
    setCityValue(defaultCity ?? "");
  }, [defaultCity]);

  const propStates = Array.isArray(stateSelect?.options) ? stateSelect.options : [];
  const propDistances = Array.isArray(distanceSelect?.options) ? distanceSelect.options : [];
  
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setFetchError(null);

    Promise.all([
      fetchOptions({ signal: ac.signal, params: { sheet: "states" } }),
      fetchOptions({
        signal: ac.signal,
        params: { sheet: "locator-distances" },
      }),
    ])
      .then(([statesData, distancesData]) => {
        const fetchedStates = Array.isArray(statesData?.options)
          ? statesData.options
          : [];
        const fetchedDistances = Array.isArray(distancesData?.options)
          ? distancesData.options
          : [];

        const sortedDistances = [...fetchedDistances].sort(
          (a, b) => Number(a.value) - Number(b.value),
        );

        const finalStates = fetchedStates.length ? fetchedStates : propStates;
        const finalDistances = sortedDistances.length
          ? sortedDistances
          : propDistances;

        setStateOptions(finalStates);
        setDistanceOptions(finalDistances);

        if (!selectedState && defaultState) setSelectedState(defaultState);
        if (!selectedDistance && defaultDistance)
          setSelectedDistance(defaultDistance);
      })
      .catch(() => {
        setStateOptions(propStates);
        setDistanceOptions(propDistances);
        setFetchError("Unable to load form options.");
      })
      .finally(() => {
        setLoading(false);

        // Auto-submit after all form values have been set
        if (isResultsOnly) {
          // Use setTimeout to ensure state updates have been applied
          setTimeout(() => {
            if (formRef.current && submitBtn.current) {
              submitBtn.current.click();
            }
          }, 0);
        }
      });

    return () => ac.abort();
  }, [isResultsOnly]);

  const handleSubmit = e => e.preventDefault();

  return (
    <form
      ref={formRef}
      className="sc-form js-form find-office-form"
      data-form-type="findOffice"
      action={formAction}
      method={method}
      onSubmit={handleSubmit}
      data-agent-types={selectedAgentTypes}
      data-pagination-target="#office-pagination"
      style={{ display: isResultsOnly ? "none" : "block" }}
    >
      <input type="hidden" name="marketArea" value={selectedMarketArea} />
      {label && <h2 className="sc-form__title">{label}</h2>}
      <div className="find-office-form__inputs-container">
        <div>
          <Autocomplete
            name="address"
            placeholder="Address"
            required
            dataKey="address"
            data-city-target='input[name="city"]'
            data-state-target='select[name="state-select"]'
          />
        </div>
        <div>
          <TextInput
            {...city}
            placeholder={city?.placeholder ?? "City"}
            value={cityValue}
            onChange={e => setCityValue(e.target.value)}
          />
        </div>
      </div>
      <div className="find-office-form__selects-container">
        <div className="find-office-form__select">
          <SelectInput
            {...stateSelect}
            options={stateOptions}
            label=""
            placeholder={
              loading
                ? "Loading states…"
                : (stateSelect?.placeholder ?? "Select a State")
            }
            name={stateSelect?.name ?? "state-select"}
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}
            disabled={loading || !!fetchError}
          />
        </div>

        <div className="find-office-form__select">
          <SelectInput
            {...distanceSelect}
            options={distanceOptions}
            label=""
            placeholder={
              loading
                ? "Loading distances…"
                : (distanceSelect?.placeholder ?? "Select a Distance")
            }
            name={distanceSelect?.name ?? "distance-select"}
            value={selectedDistance}
            onChange={e => setSelectedDistance(e.target.value)}
            disabled={loading || !!fetchError}
          />
        </div>
      </div>
      <GeneralError />
      <div className="sc-form__button-row">
        <input
          ref={submitBtn}
          type="submit"
          className="btn btn--primary"
          value={submitLabel}
        />
      </div>
    </form>
  );
};

export default FindOfficeForm;